// Seletor de templates WhatsApp APROVADOS (HSM). Fora da janela de 24h / ao iniciar
// conversa, o WhatsApp só entrega template aprovado — então aqui o vendedor escolhe
// de uma lista buscada da WABA (não digita nome livre que a Meta recusaria).
// Busca por nome/corpo; quando o template tem variáveis ({{1}}…) pede os valores antes.
// Presentational: recebe a lista já carregada (conversa OU loja) — não busca sozinho.
import { useMemo, useState } from 'react';
import { SearchIcon, XIcon } from '@/components/icons';
import type { TemplatesResponse, WhatsappTemplate } from '../api';
import styles from './TemplatePicker.module.css';

interface Props {
  data?: TemplatesResponse;
  isLoading?: boolean;
  isError?: boolean;
  /** Dispara o envio do template escolhido (name + idioma + variáveis preenchidas). */
  onSend: (name: string, language: string, variables: Record<string, string>) => void;
  onClose?: () => void;
}

/** `saudacao_inicial` → "Saudação inicial" (título amigável; o nome cru fica de legenda). */
function humanize(name: string): string {
  const s = name.replace(/[_-]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Substitui {{n}} pelos valores digitados (preview do que vai ser enviado). */
function fillPreview(body: string, vars: Record<string, string>): string {
  return body.replace(/\{\{\s*(\d+)\s*\}\}/g, (_m, n: string) => vars[n]?.trim() || `{{${n}}}`);
}

function slots(count: number): string[] {
  return Array.from({ length: count }, (_, i) => String(i + 1));
}

export function TemplatePicker({ data, isLoading, isError, onSend, onClose }: Props) {
  const [search, setSearch] = useState('');
  // Template com o formulário de variáveis aberto (chave name|language) + valores.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [vars, setVars] = useState<Record<string, string>>({});

  const items = useMemo(() => data?.items ?? [], [data]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (t) => t.name.toLowerCase().includes(q) || t.bodyText.toLowerCase().includes(q),
    );
  }, [items, search]);

  const keyOf = (t: WhatsappTemplate) => `${t.name}|${t.language}`;

  const resetForm = () => {
    setExpandedKey(null);
    setVars({});
  };

  const handleSend = (t: WhatsappTemplate) => {
    // 1º clique num template com variáveis abre o formulário; 2º clique envia.
    if (t.variableCount > 0 && expandedKey !== keyOf(t)) {
      setExpandedKey(keyOf(t));
      setVars({});
      return;
    }
    onSend(t.name, t.language, t.variableCount > 0 ? vars : {});
    resetForm();
  };

  const varsComplete = (t: WhatsappTemplate) =>
    slots(t.variableCount).every((n) => (vars[n] ?? '').trim().length > 0);

  return (
    <div className={styles.picker} role="dialog" aria-label="Templates aprovados">
      <div className={styles.header}>
        <span className={styles.title}>Templates aprovados</span>
        {onClose && (
          <button type="button" className={styles.close} aria-label="Fechar templates" onClick={onClose}>
            <XIcon size={16} />
          </button>
        )}
      </div>

      <div className={styles.searchRow}>
        <SearchIcon size={16} />
        <input
          className={styles.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Pesquisar Templates"
          aria-label="Pesquisar templates"
        />
      </div>

      <div className={styles.list}>
        {isLoading && <p className={styles.state}>Carregando templates…</p>}

        {!isLoading && (isError || data?.reason === 'FETCH_FAILED') && (
          <p className={styles.state}>Não foi possível carregar os templates. Tente de novo.</p>
        )}

        {!isLoading && data?.reason === 'NOT_CONNECTED' && (
          <p className={styles.state}>
            Conecte o WhatsApp desta loja (Configurações → Conectar via Facebook) para usar templates.
          </p>
        )}

        {!isLoading && data?.reason === 'TOKEN_INVALID' && (
          <p className={styles.state}>Token do WhatsApp expirado: renove para carregar os templates.</p>
        )}

        {!isLoading && data?.connected && !data.reason && items.length === 0 && (
          <p className={styles.state}>Nenhum template aprovado nesta conta do WhatsApp.</p>
        )}

        {!isLoading && filtered.length === 0 && items.length > 0 && (
          <p className={styles.state}>Nenhum template corresponde à busca.</p>
        )}

        {filtered.map((t) => {
          const open = expandedKey === keyOf(t);
          const blocked = open && t.variableCount > 0 && !varsComplete(t);
          return (
            <div className={styles.row} key={keyOf(t)}>
              <div className={styles.rowMain}>
                <div className={styles.rowText}>
                  <span className={styles.name}>{humanize(t.name)}</span>
                  <span className={styles.preview}>
                    {open ? fillPreview(t.bodyText, vars) : t.bodyText || '—'}
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.sendBtn}
                  onClick={() => handleSend(t)}
                  disabled={blocked}
                >
                  {t.variableCount > 0 && !open ? 'Preencher' : 'Enviar'}
                </button>
              </div>

              {open && t.variableCount > 0 && (
                <div className={styles.vars}>
                  {slots(t.variableCount).map((n) => (
                    <input
                      key={n}
                      className={styles.varInput}
                      value={vars[n] ?? ''}
                      onChange={(e) => setVars((v) => ({ ...v, [n]: e.target.value }))}
                      placeholder={`Variável {{${n}}}`}
                      aria-label={`Variável ${n}`}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
